import { collection, getDocs, doc, getDoc } from 'firebase/firestore';
import { db } from '../firebase'; // Shared Firestore instance

/**
 * Fetches the latest subscription and its limits for a user.
 * Adds diagnostics + returns models (if present) for easier debugging.
 */
export async function fetchSubscription(uid) {
  if (!uid) return { subscription: null, limits: null, models: [] };

  const subsRef = collection(db, 'users', uid, 'Subscription');
  const subsSnap = await getDocs(subsRef);

  const subscriptionDocs = [];
  subsSnap.forEach((docSnap) => {
    const data = docSnap.data();
    subscriptionDocs.push({ id: docSnap.id, ...data });
  });

  if (!subscriptionDocs.length) {
    return { subscription: null, limits: null, models: [] };
  }

  // Pick latest by startsFrom; if none have startsFrom keep first.
  let latestDoc = null;
  let latestTs = null;
  for (const d of subscriptionDocs) {
    const ts = d.startsFrom?.toMillis?.() ? d.startsFrom.toMillis() : null;
    if (ts !== null) {
      if (latestTs === null || ts > latestTs) {
        latestTs = ts;
        latestDoc = d;
      }
    } else if (!latestDoc) {
      latestDoc = d;
    }
  }

  // Fallback: if chosen latestDoc has no models but another doc does, prefer one with models
  if (!Array.isArray(latestDoc.models) || latestDoc.models.length === 0) {
    const withModels = subscriptionDocs
      .filter(d => Array.isArray(d.models) && d.models.length)
      .sort((a,b) => (b.startsFrom?.toMillis?.()||0) - (a.startsFrom?.toMillis?.()||0));
    if (withModels.length) {
      latestDoc = withModels[0];
    }
  }

  // Fetch limits (same logic as before)
  const limitsRef = collection(db, 'users', uid, 'Subscription', latestDoc.id, 'Limits');
  const limitsSnap = await getDocs(limitsRef);
  let limitsDoc = null;
  limitsSnap.forEach((docSnap) => {
    const data = docSnap.data();
    limitsDoc = { id: docSnap.id, ...data };
  });

  const models = Array.isArray(latestDoc.models) ? latestDoc.models : [];

  return {
    subscription: latestDoc,
    limits: limitsDoc,
    models,
  };
}

/**
 * Fetches the permitted models from the latest subscription with fallbacks.
 * 1. Use subscription.models if present
 * 2. If empty, attempt to read models from user root doc (optional future placement)
 */
export async function fetchPermittedModels(uid) {
  if (!uid) return [];
  const PLAN_MODEL_MAP = {
    Free: [
      'gemini-2.5-flash-lite',
      'grok-3-mini'
    ],
    Lite: [
      'gemini-2.5-flash-lite',
      'grok-3-mini',
      'gemini-2.5-flash',
      'gpt-5-mini'
    ],
    Plus: [
      'gemini-2.5-flash-lite',
      'grok-3-mini',
      'gemini-2.5-flash',
      'gpt-5-mini',
      'gemini-2.5-pro',
      'gpt-5'
    ]
  };
  try {
    const { subscription, models } = await fetchSubscription(uid);
    if (Array.isArray(models) && models.length) {
      return models;
    }
    const plan = subscription?.plan;
    if (plan) {
      const normalizedPlan = String(plan).trim();
      const fallback = PLAN_MODEL_MAP[normalizedPlan] || PLAN_MODEL_MAP[normalizedPlan.charAt(0).toUpperCase() + normalizedPlan.slice(1)] || [];
      if (fallback.length) {
        return fallback;
      }
    }
    // Fallback: look at root user doc (in case models saved there)
    try {
      const userRef = doc(db, 'users', uid);
      const userSnap = await getDoc(userRef);
      const userData = userSnap.exists() ? userSnap.data() : null;
      const rootModels = userData?.models;
      if (Array.isArray(rootModels) && rootModels.length) {
        return rootModels;
      }
    } catch (rootErr) {
      // Silent fallback failure
    }
  } catch (e) {
    console.warn('fetchPermittedModels failed', e);
  }
  return [];
}
